import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import {
  type BusinessOperationTraceV1,
  BusinessOperationTraceV1Schema,
  isBusinessOperationTraceV1,
} from "./business-operation.js";
import { PurchaseOrderNumberSchema } from "./purchase-order-number.js";
import {
  isPurchaseInventoryValuationSnapshot,
  isPurchaseReceiptRecord,
  PurchaseInventoryValuationSnapshotSchema,
  type PurchaseReceiptRecord,
  PurchaseReceiptRecordSchema,
} from "./purchase-order-receiving.js";
import {
  isPurchasePayableReconciliationRecord,
  isSupplierPaymentRecord,
  PurchasePayableProjectionStatusSchema,
  PurchasePayableReconciliationRecordSchema,
  type SupplierPaymentActivity,
  SupplierPaymentActivitySchema,
  SupplierPaymentMethodSchema,
  type SupplierPaymentRecord,
  SupplierPaymentRecordSchema,
} from "./purchase-payable.js";
import {
  deriveSupplierCreditNumber,
  deriveSupplierPaymentReversalNumber,
  deriveSupplierRefundNumber,
  isBusinessNumberSourceId,
  SupplierCreditNumberSchema,
  SupplierPaymentReversalNumberSchema,
  SupplierRefundNumberSchema,
} from "./purchase-payable-number.js";
import { PurchaseReceiptNumberSchema } from "./purchase-receipt-number.js";
import {
  allocateMovingAverageOutboundFen,
  isPurchaseReturnRecord,
  PurchaseReturnRecordSchema,
} from "./purchase-return.js";

const StableIdSchema = Type.String({
  minLength: 1,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
});
const CanonicalTextPattern = "^(?:\\S|\\S[\\s\\S]*\\S)$";
const CanonicalTextSchema = (maximum: number) =>
  Type.String({ minLength: 1, maxLength: maximum, pattern: CanonicalTextPattern });
const BusinessDateSchema = Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" });
const TimestampSchema = Type.String({ minLength: 20, maxLength: 64 });
const AmountFenSchema = Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER });
const PositiveAmountFenSchema = Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER });
const QuantitySchema = Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER });
const NonNegativeQuantitySchema = Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER });
const VersionSchema = Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER });
const IdempotencyKeySchema = Type.String({
  minLength: 8,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{7,127}$",
});
const SupplierDocumentNumberSchema = Type.String({
  minLength: 1,
  maxLength: 64,
  pattern: "^[^\\u0000-\\u001F\\u007F]+$",
});

export const RecordSupplierCreditLineRequestSchema = Type.Object(
  {
    receiptLineId: StableIdSchema,
    returnedBaseQuantity: QuantitySchema,
    creditSubtotalFen: AmountFenSchema,
    creditTaxFen: AmountFenSchema,
  },
  { additionalProperties: false },
);
export type RecordSupplierCreditLineRequest = Static<typeof RecordSupplierCreditLineRequestSchema>;

export const RecordSupplierCreditRequestSchema = Type.Object(
  {
    creditId: StableIdSchema,
    receiptId: StableIdSchema,
    reconciliationId: StableIdSchema,
    businessDate: BusinessDateSchema,
    supplierCreditNoteNumber: SupplierDocumentNumberSchema,
    creditNoteDate: BusinessDateSchema,
    reason: CanonicalTextSchema(200),
    lines: Type.Array(RecordSupplierCreditLineRequestSchema, { minItems: 1, maxItems: 200 }),
    expectedPayableBalanceVersion: VersionSchema,
    operationTrace: BusinessOperationTraceV1Schema,
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type RecordSupplierCreditRequest = Static<typeof RecordSupplierCreditRequestSchema>;

export const RecordSupplierRefundRequestSchema = Type.Object(
  {
    refundId: StableIdSchema,
    receiptId: StableIdSchema,
    reconciliationId: StableIdSchema,
    businessDate: BusinessDateSchema,
    amountFen: PositiveAmountFenSchema,
    method: SupplierPaymentMethodSchema,
    externalReference: Type.Union([CanonicalTextSchema(80), Type.Null()]),
    note: Type.Union([CanonicalTextSchema(200), Type.Null()]),
    expectedPayableBalanceVersion: VersionSchema,
    operationTrace: BusinessOperationTraceV1Schema,
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type RecordSupplierRefundRequest = Static<typeof RecordSupplierRefundRequestSchema>;

export const ReverseSupplierPaymentRequestSchema = Type.Object(
  {
    reversalId: StableIdSchema,
    receiptId: StableIdSchema,
    reconciliationId: StableIdSchema,
    paymentId: StableIdSchema,
    businessDate: BusinessDateSchema,
    reason: CanonicalTextSchema(200),
    expectedPayableBalanceVersion: VersionSchema,
    operationTrace: BusinessOperationTraceV1Schema,
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type ReverseSupplierPaymentRequest = Static<typeof ReverseSupplierPaymentRequestSchema>;

export const SupplierCreditWorkspaceLineSchema = Type.Object(
  {
    receiptLineId: StableIdSchema,
    skuId: StableIdSchema,
    productName: CanonicalTextSchema(80),
    productVersion: VersionSchema,
    baseUnitName: CanonicalTextSchema(32),
    receivedBaseQuantity: QuantitySchema,
    preReconciliationReturnedBaseQuantity: NonNegativeQuantitySchema,
    creditedReturnedBaseQuantity: NonNegativeQuantitySchema,
    creditableBaseQuantity: NonNegativeQuantitySchema,
    inventoryQuantity: NonNegativeQuantitySchema,
    inventoryVersion: VersionSchema,
    valuation: PurchaseInventoryValuationSnapshotSchema,
  },
  { additionalProperties: false },
);
export type SupplierCreditWorkspaceLine = Static<typeof SupplierCreditWorkspaceLineSchema>;

export const SupplierCreditLineRecordSchema = Type.Object(
  {
    receiptLineId: StableIdSchema,
    movementId: StableIdSchema,
    skuId: StableIdSchema,
    productName: CanonicalTextSchema(80),
    productVersion: VersionSchema,
    baseUnitName: CanonicalTextSchema(32),
    sourceReceivedBaseQuantity: QuantitySchema,
    preReconciliationReturnedBaseQuantity: NonNegativeQuantitySchema,
    creditedReturnedBaseQuantityBefore: NonNegativeQuantitySchema,
    returnedBaseQuantity: QuantitySchema,
    creditedReturnedBaseQuantityAfter: QuantitySchema,
    creditableBaseQuantityAfter: NonNegativeQuantitySchema,
    creditSubtotalFen: AmountFenSchema,
    creditTaxFen: AmountFenSchema,
    creditTotalFen: PositiveAmountFenSchema,
    inventoryQuantityBefore: QuantitySchema,
    inventoryQuantityAfter: NonNegativeQuantitySchema,
    inventoryVersionBefore: VersionSchema,
    inventoryVersionAfter: VersionSchema,
    valuationBefore: PurchaseInventoryValuationSnapshotSchema,
    valuationAfter: PurchaseInventoryValuationSnapshotSchema,
    inventoryValueDecreaseFen: AmountFenSchema,
    purchasePriceVarianceFen: Type.Integer({
      minimum: -Number.MAX_SAFE_INTEGER,
      maximum: Number.MAX_SAFE_INTEGER,
    }),
  },
  { additionalProperties: false },
);
export type SupplierCreditLineRecord = Static<typeof SupplierCreditLineRecordSchema>;

export const SupplierCreditRecordSchema = Type.Object(
  {
    creditId: StableIdSchema,
    creditNumber: SupplierCreditNumberSchema,
    supplierCreditNoteNumber: SupplierDocumentNumberSchema,
    creditNoteDate: BusinessDateSchema,
    receiptId: StableIdSchema,
    receiptNumber: PurchaseReceiptNumberSchema,
    purchaseOrderId: StableIdSchema,
    purchaseOrderNumber: PurchaseOrderNumberSchema,
    reconciliationId: StableIdSchema,
    supplierId: StableIdSchema,
    supplierName: CanonicalTextSchema(80),
    businessDate: BusinessDateSchema,
    reason: CanonicalTextSchema(200),
    lines: Type.Array(SupplierCreditLineRecordSchema, { minItems: 1, maxItems: 200 }),
    creditSubtotalFen: AmountFenSchema,
    creditTaxFen: AmountFenSchema,
    creditTotalFen: PositiveAmountFenSchema,
    inventoryValueDecreaseFen: AmountFenSchema,
    purchasePriceVarianceFen: Type.Integer({
      minimum: -Number.MAX_SAFE_INTEGER,
      maximum: Number.MAX_SAFE_INTEGER,
    }),
    payableOutstandingFenBefore: AmountFenSchema,
    payableOutstandingFenAfter: AmountFenSchema,
    supplierRefundDueFenBefore: AmountFenSchema,
    supplierRefundDueFenAfter: AmountFenSchema,
    payableBalanceVersionBefore: VersionSchema,
    payableBalanceVersionAfter: VersionSchema,
    creditorActorId: StableIdSchema,
    operationTrace: BusinessOperationTraceV1Schema,
    recordedAt: TimestampSchema,
    evidence: Type.Literal("STORE_ACCEPTED"),
  },
  { additionalProperties: false },
);
export type SupplierCreditRecord = Static<typeof SupplierCreditRecordSchema>;

export const SupplierRefundRecordSchema = Type.Object(
  {
    refundId: StableIdSchema,
    refundNumber: SupplierRefundNumberSchema,
    receiptId: StableIdSchema,
    receiptNumber: PurchaseReceiptNumberSchema,
    purchaseOrderId: StableIdSchema,
    purchaseOrderNumber: PurchaseOrderNumberSchema,
    reconciliationId: StableIdSchema,
    supplierId: StableIdSchema,
    supplierName: CanonicalTextSchema(80),
    businessDate: BusinessDateSchema,
    amountFen: PositiveAmountFenSchema,
    method: SupplierPaymentMethodSchema,
    externalReference: Type.Union([CanonicalTextSchema(80), Type.Null()]),
    note: Type.Union([CanonicalTextSchema(200), Type.Null()]),
    payableOutstandingFenBefore: Type.Literal(0),
    payableOutstandingFenAfter: Type.Literal(0),
    supplierRefundDueFenBefore: PositiveAmountFenSchema,
    supplierRefundDueFenAfter: AmountFenSchema,
    payableBalanceVersionBefore: VersionSchema,
    payableBalanceVersionAfter: VersionSchema,
    settlementEffect: Type.Literal("EXTERNAL_SUPPLIER_REFUND_RECORDED"),
    receiverActorId: StableIdSchema,
    operationTrace: BusinessOperationTraceV1Schema,
    recordedAt: TimestampSchema,
    evidence: Type.Literal("STORE_ACCEPTED"),
  },
  { additionalProperties: false },
);
export type SupplierRefundRecord = Static<typeof SupplierRefundRecordSchema>;

export const SupplierPaymentReversalRecordSchema = Type.Object(
  {
    reversalId: StableIdSchema,
    reversalNumber: SupplierPaymentReversalNumberSchema,
    receiptId: StableIdSchema,
    receiptNumber: PurchaseReceiptNumberSchema,
    purchaseOrderId: StableIdSchema,
    purchaseOrderNumber: PurchaseOrderNumberSchema,
    reconciliationId: StableIdSchema,
    paymentId: StableIdSchema,
    paymentNumber: Type.String({ pattern: "^FK-[0-9]{8}-[0-9A-F]{12}$" }),
    supplierId: StableIdSchema,
    supplierName: CanonicalTextSchema(80),
    businessDate: BusinessDateSchema,
    amountFen: PositiveAmountFenSchema,
    reason: CanonicalTextSchema(200),
    payableOutstandingFenBefore: AmountFenSchema,
    payableOutstandingFenAfter: AmountFenSchema,
    supplierRefundDueFenBefore: AmountFenSchema,
    supplierRefundDueFenAfter: AmountFenSchema,
    payableBalanceVersionBefore: VersionSchema,
    payableBalanceVersionAfter: VersionSchema,
    settlementEffect: Type.Literal("SUPPLIER_PAYMENT_RECORD_REVERSED"),
    reverserActorId: StableIdSchema,
    operationTrace: BusinessOperationTraceV1Schema,
    reversedAt: TimestampSchema,
    evidence: Type.Literal("STORE_ACCEPTED"),
  },
  { additionalProperties: false },
);
export type SupplierPaymentReversalRecord = Static<typeof SupplierPaymentReversalRecordSchema>;

export const SupplierSettlementLastActivitySchema = Type.Object(
  {
    kind: Type.Union([
      Type.Literal("RECONCILIATION"),
      Type.Literal("PAYMENT"),
      Type.Literal("CREDIT"),
      Type.Literal("REFUND"),
      Type.Literal("PAYMENT_REVERSAL"),
    ]),
    businessId: StableIdSchema,
    commandId: StableIdSchema,
    gatewaySequence: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
  },
  { additionalProperties: false },
);
export type SupplierSettlementLastActivity = Static<typeof SupplierSettlementLastActivitySchema>;

export const SupplierCreditSettlementWorkspaceSchema = Type.Object(
  {
    receipt: PurchaseReceiptRecordSchema,
    returns: Type.Array(PurchaseReturnRecordSchema, { maxItems: 500 }),
    reconciliation: PurchasePayableReconciliationRecordSchema,
    payments: Type.Array(SupplierPaymentRecordSchema, { maxItems: 500 }),
    paymentActivities: Type.Array(SupplierPaymentActivitySchema, { maxItems: 500 }),
    credits: Type.Array(SupplierCreditRecordSchema, { maxItems: 500 }),
    refunds: Type.Array(SupplierRefundRecordSchema, { maxItems: 500 }),
    paymentReversals: Type.Array(SupplierPaymentReversalRecordSchema, { maxItems: 500 }),
    lines: Type.Array(SupplierCreditWorkspaceLineSchema, { minItems: 1, maxItems: 200 }),
    creditedSubtotalFen: AmountFenSchema,
    creditedTaxFen: AmountFenSchema,
    creditableSubtotalFen: AmountFenSchema,
    creditableTaxFen: AmountFenSchema,
    payableOutstandingFen: AmountFenSchema,
    supplierRefundDueFen: AmountFenSchema,
    payableBalanceVersion: VersionSchema,
    lastCommandId: StableIdSchema,
    lastActivity: SupplierSettlementLastActivitySchema,
    canCredit: Type.Boolean(),
    canRefund: Type.Boolean(),
    canReversePayment: Type.Boolean(),
    blocker: Type.Union([CanonicalTextSchema(300), Type.Null()]),
    generatedAt: TimestampSchema,
  },
  { additionalProperties: false },
);
export type SupplierCreditSettlementWorkspace = Static<
  typeof SupplierCreditSettlementWorkspaceSchema
>;

export const SupplierCreditWriteResultSchema = Type.Object(
  {
    credit: SupplierCreditRecordSchema,
    cloudProjectionStatus: PurchasePayableProjectionStatusSchema,
  },
  { additionalProperties: false },
);
export type SupplierCreditWriteResult = Static<typeof SupplierCreditWriteResultSchema>;
export const SupplierRefundWriteResultSchema = Type.Object(
  {
    refund: SupplierRefundRecordSchema,
    cloudProjectionStatus: PurchasePayableProjectionStatusSchema,
  },
  { additionalProperties: false },
);
export type SupplierRefundWriteResult = Static<typeof SupplierRefundWriteResultSchema>;
export const SupplierPaymentReversalWriteResultSchema = Type.Object(
  {
    reversal: SupplierPaymentReversalRecordSchema,
    cloudProjectionStatus: PurchasePayableProjectionStatusSchema,
  },
  { additionalProperties: false },
);
export type SupplierPaymentReversalWriteResult = Static<
  typeof SupplierPaymentReversalWriteResultSchema
>;

export const SupplierCreditBusinessEventPayloadSchema = Type.Object(
  { credit: SupplierCreditRecordSchema },
  { additionalProperties: false },
);
export const SupplierRefundBusinessEventPayloadSchema = Type.Object(
  { refund: SupplierRefundRecordSchema },
  { additionalProperties: false },
);
export const SupplierPaymentReversalBusinessEventPayloadSchema = Type.Object(
  { reversal: SupplierPaymentReversalRecordSchema },
  { additionalProperties: false },
);

function isBusinessDate(value: string): boolean {
  const milliseconds = Date.parse(`${value}T00:00:00.000Z`);
  return (
    Number.isFinite(milliseconds) &&
    new Date(milliseconds).toISOString() === `${value}T00:00:00.000Z`
  );
}

function isTimestamp(value: string): boolean {
  const milliseconds = Date.parse(value);
  return Number.isFinite(milliseconds) && new Date(milliseconds).toISOString() === value;
}

function safeSum(values: number[]): number | null {
  let total = 0;
  for (const value of values) {
    total += value;
    if (!Number.isSafeInteger(total)) return null;
  }
  return total;
}

function traceMatches(
  trace: BusinessOperationTraceV1,
  receipt: PurchaseReceiptRecord,
  idempotencyKey: string,
  causationId: string,
): boolean {
  return (
    isBusinessOperationTraceV1(trace) &&
    trace.commandId === idempotencyKey &&
    trace.idempotencyKey === idempotencyKey &&
    trace.correlationId === `purchase-payable:${receipt.receiptId}` &&
    trace.causationId === causationId &&
    trace.businessNumber?.kind === "PURCHASE_ORDER" &&
    trace.businessNumber.value === receipt.purchaseOrderNumber
  );
}

export function applySupplierSettlementDelta(
  payableOutstandingFen: number,
  supplierRefundDueFen: number,
  netDeltaFen: number,
): { payableOutstandingFen: number; supplierRefundDueFen: number } | null {
  if (
    !Number.isSafeInteger(payableOutstandingFen) ||
    !Number.isSafeInteger(supplierRefundDueFen) ||
    !Number.isSafeInteger(netDeltaFen) ||
    payableOutstandingFen < 0 ||
    supplierRefundDueFen < 0 ||
    (payableOutstandingFen > 0 && supplierRefundDueFen > 0)
  ) {
    return null;
  }
  const net = payableOutstandingFen - supplierRefundDueFen + netDeltaFen;
  if (!Number.isSafeInteger(net)) return null;
  return net >= 0
    ? { payableOutstandingFen: net, supplierRefundDueFen: 0 }
    : { payableOutstandingFen: 0, supplierRefundDueFen: -net };
}

function paymentForReversal(
  workspace: SupplierCreditSettlementWorkspace,
  paymentId: string,
): SupplierPaymentRecord | null {
  if (workspace.paymentReversals.some((item) => item.paymentId === paymentId)) return null;
  return workspace.payments.find((item) => item.paymentId === paymentId) ?? null;
}

export function isRecordSupplierCreditRequest(
  value: unknown,
): value is RecordSupplierCreditRequest {
  if (!Value.Check(RecordSupplierCreditRequestSchema, value)) return false;
  const request = value as RecordSupplierCreditRequest;
  const lineIds = request.lines.map((line) => line.receiptLineId);
  const totals = request.lines.map((line) => line.creditSubtotalFen + line.creditTaxFen);
  return (
    isBusinessDate(request.businessDate) &&
    isBusinessNumberSourceId(request.creditId) &&
    isBusinessDate(request.creditNoteDate) &&
    request.businessDate >= request.creditNoteDate &&
    request.supplierCreditNoteNumber === request.supplierCreditNoteNumber.trim() &&
    new Set(lineIds).size === lineIds.length &&
    totals.every((total) => Number.isSafeInteger(total) && total > 0) &&
    (safeSum(totals) ?? 0) > 0 &&
    request.operationTrace.commandId === request.idempotencyKey &&
    request.operationTrace.idempotencyKey === request.idempotencyKey
  );
}

export function isRecordSupplierRefundRequest(
  value: unknown,
): value is RecordSupplierRefundRequest {
  if (!Value.Check(RecordSupplierRefundRequestSchema, value)) return false;
  const request = value as RecordSupplierRefundRequest;
  return (
    isBusinessDate(request.businessDate) &&
    isBusinessNumberSourceId(request.refundId) &&
    (request.method === "CASH" || request.externalReference !== null) &&
    request.operationTrace.commandId === request.idempotencyKey &&
    request.operationTrace.idempotencyKey === request.idempotencyKey
  );
}

export function isReverseSupplierPaymentRequest(
  value: unknown,
): value is ReverseSupplierPaymentRequest {
  if (!Value.Check(ReverseSupplierPaymentRequestSchema, value)) return false;
  const request = value as ReverseSupplierPaymentRequest;
  return (
    isBusinessDate(request.businessDate) &&
    isBusinessNumberSourceId(request.reversalId) &&
    request.operationTrace.commandId === request.idempotencyKey &&
    request.operationTrace.idempotencyKey === request.idempotencyKey
  );
}

function activityCommand(
  workspace: SupplierCreditSettlementWorkspace,
  activity: SupplierSettlementLastActivity,
): string | null {
  if (activity.kind === "RECONCILIATION") {
    return activity.businessId === workspace.reconciliation.reconciliationId
      ? workspace.reconciliation.operationTrace.commandId
      : null;
  }
  if (activity.kind === "PAYMENT") {
    return (
      workspace.paymentActivities.find((item) => item.paymentId === activity.businessId)
        ?.commandId ?? null
    );
  }
  const collections: Record<
    Exclude<SupplierSettlementLastActivity["kind"], "RECONCILIATION" | "PAYMENT">,
    Array<{ id: string; command: string }>
  > = {
    CREDIT: workspace.credits.map((item) => ({
      id: item.creditId,
      command: item.operationTrace.commandId,
    })),
    REFUND: workspace.refunds.map((item) => ({
      id: item.refundId,
      command: item.operationTrace.commandId,
    })),
    PAYMENT_REVERSAL: workspace.paymentReversals.map((item) => ({
      id: item.reversalId,
      command: item.operationTrace.commandId,
    })),
  };
  return (
    collections[activity.kind].find((item) => item.id === activity.businessId)?.command ?? null
  );
}

export function isSupplierCreditSettlementWorkspace(
  value: unknown,
): value is SupplierCreditSettlementWorkspace {
  if (!Value.Check(SupplierCreditSettlementWorkspaceSchema, value)) return false;
  const workspace = value as SupplierCreditSettlementWorkspace;
  if (
    !isTimestamp(workspace.generatedAt) ||
    !isPurchaseReceiptRecord(workspace.receipt) ||
    !isPurchasePayableReconciliationRecord(workspace.reconciliation) ||
    workspace.reconciliation.receiptId !== workspace.receipt.receiptId ||
    !workspace.returns.every(
      (item) => isPurchaseReturnRecord(item) && item.receiptId === workspace.receipt.receiptId,
    ) ||
    !workspace.payments.every(
      (item) =>
        isSupplierPaymentRecord(item) &&
        item.receiptId === workspace.receipt.receiptId &&
        item.reconciliationId === workspace.reconciliation.reconciliationId,
    ) ||
    new Set(workspace.paymentActivities.map((item) => item.paymentId)).size !==
      workspace.paymentActivities.length ||
    !workspace.paymentActivities.every(
      (item, index) =>
        isTimestamp(item.recordedAt) &&
        (item.method === "CASH" || item.externalReference !== null) &&
        (index === 0 ||
          (workspace.paymentActivities[index - 1]?.gatewaySequence ?? Number.MAX_SAFE_INTEGER) <
            item.gatewaySequence),
    ) ||
    workspace.paymentActivities.filter((item) => item.kind === "SINGLE").length !==
      workspace.payments.length ||
    !workspace.payments.every((payment) =>
      workspace.paymentActivities.some(
        (activity) =>
          activity.kind === "SINGLE" &&
          activity.paymentId === payment.paymentId &&
          activity.paymentNumber === payment.paymentNumber &&
          activity.amountFen === payment.amountFen &&
          activity.commandId === payment.operationTrace.commandId,
      ),
    ) ||
    !workspace.credits.every(
      (item) =>
        isSupplierCreditRecord(item) &&
        item.receiptId === workspace.receipt.receiptId &&
        item.reconciliationId === workspace.reconciliation.reconciliationId &&
        item.supplierId === workspace.reconciliation.supplierId,
    ) ||
    !workspace.refunds.every(
      (item) =>
        isSupplierRefundRecord(item) &&
        item.receiptId === workspace.receipt.receiptId &&
        item.reconciliationId === workspace.reconciliation.reconciliationId &&
        item.supplierId === workspace.reconciliation.supplierId,
    ) ||
    !workspace.paymentReversals.every(
      (item) =>
        isSupplierPaymentReversalRecord(item) &&
        item.receiptId === workspace.receipt.receiptId &&
        item.reconciliationId === workspace.reconciliation.reconciliationId &&
        item.supplierId === workspace.reconciliation.supplierId,
    ) ||
    (workspace.payableOutstandingFen > 0 && workspace.supplierRefundDueFen > 0) ||
    activityCommand(workspace, workspace.lastActivity) !== workspace.lastCommandId
  ) {
    return false;
  }
  const creditedSubtotal = safeSum(workspace.credits.map((item) => item.creditSubtotalFen));
  const creditedTax = safeSum(workspace.credits.map((item) => item.creditTaxFen));
  const paymentTotal = safeSum(
    workspace.paymentActivities.map((item: SupplierPaymentActivity) => item.amountFen),
  );
  const reversalTotal = safeSum(workspace.paymentReversals.map((item) => item.amountFen));
  const refundTotal = safeSum(workspace.refunds.map((item) => item.amountFen));
  if (
    [creditedSubtotal, creditedTax, paymentTotal, reversalTotal, refundTotal].some(
      (item) => item === null,
    )
  )
    return false;
  const obligation =
    workspace.reconciliation.invoiceTotalFen - (creditedSubtotal ?? 0) - (creditedTax ?? 0);
  const netSettled = (paymentTotal ?? 0) - (reversalTotal ?? 0) - (refundTotal ?? 0);
  if (obligation < 0 || !Number.isSafeInteger(netSettled)) return false;
  const expectedOutstanding = Math.max(obligation - netSettled, 0);
  const expectedRefundDue = Math.max(netSettled - obligation, 0);
  const lineById = new Map(workspace.receipt.lines.map((line) => [line.lineId, line]));
  const creditIds = workspace.credits.map((item) => item.creditId);
  const supplierCreditNoteKeys = workspace.credits.map((item) =>
    item.supplierCreditNoteNumber.toLocaleLowerCase("en-US"),
  );
  const refundIds = workspace.refunds.map((item) => item.refundId);
  const reversalIds = workspace.paymentReversals.map((item) => item.reversalId);
  if (
    new Set(creditIds).size !== creditIds.length ||
    new Set(supplierCreditNoteKeys).size !== supplierCreditNoteKeys.length ||
    new Set(refundIds).size !== refundIds.length ||
    new Set(reversalIds).size !== reversalIds.length ||
    workspace.credits.some((credit) =>
      credit.lines.some((line) => !lineById.has(line.receiptLineId)),
    )
  ) {
    return false;
  }
  const workspaceLineIds = workspace.lines.map((line) => line.receiptLineId);
  if (
    workspaceLineIds.length !== workspace.receipt.lines.length ||
    new Set(workspaceLineIds).size !== workspaceLineIds.length
  ) {
    return false;
  }
  const preReturned = new Map<string, number>();
  for (const purchaseReturn of workspace.returns) {
    for (const line of purchaseReturn.lines) {
      preReturned.set(
        line.receiptLineId,
        (preReturned.get(line.receiptLineId) ?? 0) + line.returnedBaseQuantity,
      );
    }
  }
  const creditedReturned = new Map<string, number>();
  for (const credit of workspace.credits) {
    for (const line of credit.lines) {
      creditedReturned.set(
        line.receiptLineId,
        (creditedReturned.get(line.receiptLineId) ?? 0) + line.returnedBaseQuantity,
      );
    }
  }
  const linesValid = workspace.lines.every((line) => {
    const source = lineById.get(line.receiptLineId);
    return Boolean(
      source &&
        isPurchaseInventoryValuationSnapshot(line.valuation) &&
        line.receivedBaseQuantity === source.receivedBaseQuantity &&
        line.preReconciliationReturnedBaseQuantity === (preReturned.get(line.receiptLineId) ?? 0) &&
        line.creditedReturnedBaseQuantity === (creditedReturned.get(line.receiptLineId) ?? 0) &&
        line.creditableBaseQuantity ===
          line.receivedBaseQuantity -
            line.preReconciliationReturnedBaseQuantity -
            line.creditedReturnedBaseQuantity &&
        line.inventoryQuantity === line.valuation.quantity,
    );
  });
  const unreversedPaymentCount = workspace.payments.filter(
    (payment) => !workspace.paymentReversals.some((item) => item.paymentId === payment.paymentId),
  ).length;
  const reversedPaymentIds = workspace.paymentReversals.map((item) => item.paymentId);
  const reversalBindingsValid =
    new Set(reversedPaymentIds).size === reversedPaymentIds.length &&
    workspace.paymentReversals.every((reversal) => {
      const payment = workspace.payments.find((item) => item.paymentId === reversal.paymentId);
      return Boolean(
        payment &&
          reversal.paymentNumber === payment.paymentNumber &&
          reversal.amountFen === payment.amountFen,
      );
    });
  return (
    linesValid &&
    reversalBindingsValid &&
    workspace.creditedSubtotalFen === creditedSubtotal &&
    workspace.creditedTaxFen === creditedTax &&
    workspace.creditableSubtotalFen ===
      workspace.reconciliation.invoiceSubtotalFen - (creditedSubtotal ?? 0) &&
    workspace.creditableTaxFen === workspace.reconciliation.invoiceTaxFen - (creditedTax ?? 0) &&
    workspace.payableOutstandingFen === expectedOutstanding &&
    workspace.supplierRefundDueFen === expectedRefundDue &&
    workspace.canCredit ===
      (workspace.credits.length < 500 &&
        workspace.creditableSubtotalFen + workspace.creditableTaxFen > 0 &&
        workspace.lines.some(
          (line) => line.creditableBaseQuantity > 0 && line.inventoryQuantity > 0,
        )) &&
    workspace.canRefund ===
      (workspace.refunds.length < 500 && workspace.supplierRefundDueFen > 0) &&
    workspace.canReversePayment ===
      (workspace.paymentReversals.length < 500 && unreversedPaymentCount > 0)
  );
}

export function isRecordSupplierCreditAgainstWorkspace(
  requestValue: unknown,
  workspaceValue: unknown,
): requestValue is RecordSupplierCreditRequest {
  if (
    !isRecordSupplierCreditRequest(requestValue) ||
    !isSupplierCreditSettlementWorkspace(workspaceValue)
  )
    return false;
  const request = requestValue as RecordSupplierCreditRequest;
  const workspace = workspaceValue as SupplierCreditSettlementWorkspace;
  const subtotal = safeSum(request.lines.map((line) => line.creditSubtotalFen));
  const tax = safeSum(request.lines.map((line) => line.creditTaxFen));
  return (
    workspace.canCredit &&
    request.receiptId === workspace.receipt.receiptId &&
    request.reconciliationId === workspace.reconciliation.reconciliationId &&
    request.businessDate >= workspace.reconciliation.businessDate &&
    request.expectedPayableBalanceVersion === workspace.payableBalanceVersion &&
    subtotal !== null &&
    tax !== null &&
    subtotal <= workspace.creditableSubtotalFen &&
    tax <= workspace.creditableTaxFen &&
    supplierCreditRequestLinesFitInventory(request.lines, workspace.lines) &&
    traceMatches(
      request.operationTrace,
      workspace.receipt,
      request.idempotencyKey,
      workspace.lastCommandId,
    )
  );
}

export function supplierCreditRequestLinesFitInventory(
  requestLines: readonly RecordSupplierCreditLineRequest[],
  workspaceLines: readonly SupplierCreditWorkspaceLine[],
): boolean {
  const lineById = new Map(workspaceLines.map((line) => [line.receiptLineId, line]));
  const remainingInventoryBySku = new Map<string, number>();
  return requestLines.every((line) => {
    const current = lineById.get(line.receiptLineId);
    if (!current || line.returnedBaseQuantity > current.creditableBaseQuantity) return false;
    const remaining = remainingInventoryBySku.get(current.skuId) ?? current.inventoryQuantity;
    if (line.returnedBaseQuantity > remaining) return false;
    remainingInventoryBySku.set(current.skuId, remaining - line.returnedBaseQuantity);
    return true;
  });
}

export function isRecordSupplierRefundAgainstWorkspace(
  requestValue: unknown,
  workspaceValue: unknown,
): requestValue is RecordSupplierRefundRequest {
  if (
    !isRecordSupplierRefundRequest(requestValue) ||
    !isSupplierCreditSettlementWorkspace(workspaceValue)
  )
    return false;
  const request = requestValue as RecordSupplierRefundRequest;
  const workspace = workspaceValue as SupplierCreditSettlementWorkspace;
  return (
    workspace.canRefund &&
    request.receiptId === workspace.receipt.receiptId &&
    request.reconciliationId === workspace.reconciliation.reconciliationId &&
    request.businessDate >= workspace.reconciliation.businessDate &&
    request.amountFen <= workspace.supplierRefundDueFen &&
    request.expectedPayableBalanceVersion === workspace.payableBalanceVersion &&
    traceMatches(
      request.operationTrace,
      workspace.receipt,
      request.idempotencyKey,
      workspace.lastCommandId,
    )
  );
}

export function isReverseSupplierPaymentAgainstWorkspace(
  requestValue: unknown,
  workspaceValue: unknown,
): requestValue is ReverseSupplierPaymentRequest {
  if (
    !isReverseSupplierPaymentRequest(requestValue) ||
    !isSupplierCreditSettlementWorkspace(workspaceValue)
  )
    return false;
  const request = requestValue as ReverseSupplierPaymentRequest;
  const workspace = workspaceValue as SupplierCreditSettlementWorkspace;
  const payment = paymentForReversal(workspace, request.paymentId);
  return Boolean(
    workspace.canReversePayment &&
      payment &&
      request.receiptId === workspace.receipt.receiptId &&
      request.reconciliationId === workspace.reconciliation.reconciliationId &&
      request.businessDate >= payment.businessDate &&
      request.expectedPayableBalanceVersion === workspace.payableBalanceVersion &&
      traceMatches(
        request.operationTrace,
        workspace.receipt,
        request.idempotencyKey,
        workspace.lastCommandId,
      ),
  );
}

function recordTraceValid(
  trace: BusinessOperationTraceV1,
  receiptId: string,
  purchaseOrderNumber: string,
): boolean {
  return (
    isBusinessOperationTraceV1(trace) &&
    trace.commandId === trace.idempotencyKey &&
    trace.correlationId === `purchase-payable:${receiptId}` &&
    trace.causationId !== null &&
    trace.businessNumber?.kind === "PURCHASE_ORDER" &&
    trace.businessNumber.value === purchaseOrderNumber
  );
}

export function isSupplierCreditRecord(value: unknown): value is SupplierCreditRecord {
  if (!Value.Check(SupplierCreditRecordSchema, value)) return false;
  const record = value as SupplierCreditRecord;
  const subtotal = safeSum(record.lines.map((line) => line.creditSubtotalFen));
  const tax = safeSum(record.lines.map((line) => line.creditTaxFen));
  const total = safeSum(record.lines.map((line) => line.creditTotalFen));
  const valueDecrease = safeSum(record.lines.map((line) => line.inventoryValueDecreaseFen));
  const variance = safeSum(record.lines.map((line) => line.purchasePriceVarianceFen));
  const next = applySupplierSettlementDelta(
    record.payableOutstandingFenBefore,
    record.supplierRefundDueFenBefore,
    -record.creditTotalFen,
  );
  const lineIds = record.lines.map((line) => line.receiptLineId);
  const linesValid = record.lines.every((line) => {
    let expectedValueDecreaseFen: number;
    try {
      expectedValueDecreaseFen = allocateMovingAverageOutboundFen(
        line.valuationBefore.totalValueFen,
        line.inventoryQuantityBefore,
        line.returnedBaseQuantity,
      );
    } catch {
      return false;
    }
    return (
      line.creditTotalFen === line.creditSubtotalFen + line.creditTaxFen &&
      line.creditedReturnedBaseQuantityAfter ===
        line.creditedReturnedBaseQuantityBefore + line.returnedBaseQuantity &&
      line.creditableBaseQuantityAfter ===
        line.sourceReceivedBaseQuantity -
          line.preReconciliationReturnedBaseQuantity -
          line.creditedReturnedBaseQuantityAfter &&
      line.inventoryQuantityAfter === line.inventoryQuantityBefore - line.returnedBaseQuantity &&
      line.inventoryVersionAfter === line.inventoryVersionBefore + 1 &&
      isPurchaseInventoryValuationSnapshot(line.valuationBefore) &&
      isPurchaseInventoryValuationSnapshot(line.valuationAfter) &&
      line.valuationBefore.quantity === line.inventoryQuantityBefore &&
      line.valuationAfter.quantity === line.inventoryQuantityAfter &&
      line.inventoryValueDecreaseFen === expectedValueDecreaseFen &&
      line.valuationAfter.totalValueFen ===
        line.valuationBefore.totalValueFen - line.inventoryValueDecreaseFen &&
      line.purchasePriceVarianceFen === line.creditSubtotalFen - line.inventoryValueDecreaseFen
    );
  });
  return Boolean(
    isBusinessDate(record.businessDate) &&
      isBusinessNumberSourceId(record.creditId) &&
      isBusinessDate(record.creditNoteDate) &&
      record.businessDate >= record.creditNoteDate &&
      record.supplierCreditNoteNumber === record.supplierCreditNoteNumber.trim() &&
      isTimestamp(record.recordedAt) &&
      new Set(lineIds).size === lineIds.length &&
      linesValid &&
      subtotal === record.creditSubtotalFen &&
      tax === record.creditTaxFen &&
      total === record.creditTotalFen &&
      valueDecrease === record.inventoryValueDecreaseFen &&
      variance === record.purchasePriceVarianceFen &&
      record.creditTotalFen === record.creditSubtotalFen + record.creditTaxFen &&
      record.creditNumber === deriveSupplierCreditNumber(record.businessDate, record.creditId) &&
      next?.payableOutstandingFen === record.payableOutstandingFenAfter &&
      next.supplierRefundDueFen === record.supplierRefundDueFenAfter &&
      record.payableBalanceVersionAfter === record.payableBalanceVersionBefore + 1 &&
      recordTraceValid(record.operationTrace, record.receiptId, record.purchaseOrderNumber),
  );
}

export function isSupplierRefundRecord(value: unknown): value is SupplierRefundRecord {
  if (!Value.Check(SupplierRefundRecordSchema, value)) return false;
  const record = value as SupplierRefundRecord;
  return (
    isBusinessDate(record.businessDate) &&
    isBusinessNumberSourceId(record.refundId) &&
    isTimestamp(record.recordedAt) &&
    record.refundNumber === deriveSupplierRefundNumber(record.businessDate, record.refundId) &&
    record.supplierRefundDueFenAfter === record.supplierRefundDueFenBefore - record.amountFen &&
    record.payableBalanceVersionAfter === record.payableBalanceVersionBefore + 1 &&
    (record.method === "CASH" || record.externalReference !== null) &&
    recordTraceValid(record.operationTrace, record.receiptId, record.purchaseOrderNumber)
  );
}

export function isSupplierPaymentReversalRecord(
  value: unknown,
): value is SupplierPaymentReversalRecord {
  if (!Value.Check(SupplierPaymentReversalRecordSchema, value)) return false;
  const record = value as SupplierPaymentReversalRecord;
  const next = applySupplierSettlementDelta(
    record.payableOutstandingFenBefore,
    record.supplierRefundDueFenBefore,
    record.amountFen,
  );
  return Boolean(
    isBusinessDate(record.businessDate) &&
      isBusinessNumberSourceId(record.reversalId) &&
      isTimestamp(record.reversedAt) &&
      record.reversalNumber ===
        deriveSupplierPaymentReversalNumber(record.businessDate, record.reversalId) &&
      next?.payableOutstandingFen === record.payableOutstandingFenAfter &&
      next.supplierRefundDueFen === record.supplierRefundDueFenAfter &&
      record.payableBalanceVersionAfter === record.payableBalanceVersionBefore + 1 &&
      recordTraceValid(record.operationTrace, record.receiptId, record.purchaseOrderNumber),
  );
}
